import Toolbar from "@mui/material/Toolbar";
import {DrawerItem} from "../../core/model/DrawerItem";
import {ReactNode, useState} from "react";
import {useThemeContext} from "@theme";
import {Stack, Typography} from "@mui/material";
import {UserActions} from "@components/user-actions";
import {translate} from "@util";
import {AppBar} from "./AppBar";
import {useDownBreakpoint} from "@hooks";
import {Drawer} from "./Drawer";
import {Chatbot} from "@components/chatbot";
import {useLocation} from "react-router-dom";

type Props = {
  appName: string;
  drawerOpen: boolean;
  drawerOpenWidth?: number;
  drawerItems: (DrawerItem | ReactNode)[];
  drawerItemIndex: number;
  userInfoBottom?: React.ReactNode;
  children: ReactNode | ReactNode[];
  onDrawerOpenChange?: (open: boolean) => void;
  onDrawerItemeSelect?: (index: number) => void;
};

export default function Dashboard(props: Props) {
  const {
    appName,
    drawerItems,
    drawerOpen: drawerOpenProp,
    drawerItemIndex,
    children,
    onDrawerOpenChange,
    onDrawerItemeSelect,
  } = props;

  const {theme} = useThemeContext();

  const [open, setOpen] = useState<boolean>(drawerOpenProp);

  const onDrawerChange = (status: boolean) => {
    setOpen(status);
    if (onDrawerOpenChange) {
      onDrawerOpenChange(status);
    }
  };

  const handleDrawerItemClick = (index: number) => {
    if (onDrawerItemeSelect) {
      onDrawerItemeSelect(index);
    }
  };

  const isDownLg = useDownBreakpoint("lg");

  const title = translate(
    (drawerItems[drawerItemIndex] as DrawerItem)?.i18n ?? "",
  );
  const location = useLocation();

  const chatbotOverlay = location.pathname === "/dashboard/home";

  return (
    <Stack
      direction={isDownLg ? "column" : "row"}
      bgcolor={theme.palette.background.default}
      id="dashboard"
      position="relative"
      overflow={"hidden"}
      minWidth={0}
      flex={"1 1 0"}>
      {isDownLg && (
        <AppBar
          drawerItems={drawerItems}
          open={open}
          drawerItemIndex={drawerItemIndex}
          onDrawerChange={onDrawerChange}
        />
      )}
      <Drawer
        appName={appName}
        drawerItems={drawerItems}
        open={open}
        drawerItemIndex={drawerItemIndex}
        onChange={onDrawerChange}
        onItemClick={handleDrawerItemClick}
        variant={isDownLg ? "temporary" : "permanent"}
      />
      {isDownLg ? (
        <>
          <Toolbar />
          {children}
        </>
      ) : (
        <Stack flexGrow={1} flex={1} minWidth={0} overflow={"auto"} pb={2}>
          <Stack
            py={1}
            px={4}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}>
            <Typography variant="h5" color="textPrimary">
              {title}
            </Typography>
            <UserActions size="medium" />
          </Stack>
          {children}
        </Stack>
      )}
      <Stack
        sx={{
          ...(chatbotOverlay && {
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 1,
          }),
        }}>
        <Chatbot />
      </Stack>
    </Stack>
  );
}
